import {
  type CreateGlobalCreditAccountInput,
  GlobalCreditAccountDetailDocument,
  type GlobalCreditAccountDetailQuery,
  type GlobalCreditAccountDetailQueryVariables,
  UpdateGlobalCreditAccountDocument,
  type UpdateGlobalCreditAccountInput,
  type UpdateGlobalCreditAccountMutation,
  type UpdateGlobalCreditAccountMutationVariables,
} from "@src/__generated__/graphql";
import { TransferGlobalCreditModalStore } from "@src/components/modules/credit-account/global-account-detail/components/transfer-credit-modal/store";
import { GlobalCreditAccountModalStore } from "@src/components/modules/credit-account/global-accounts-listing/components/modal/store";
import { FetchHelper } from "@src/helpers/apollo/fetch";
import { MutationHelper } from "@src/helpers/apollo/mutation";
import type { AppStore } from "@src/stores/AppStore";
import { nominate } from "@src/utils/formatters";
import { action, computed, makeObservable, observable } from "mobx";

export class GlobalCreditAccountDetailStore {
  @observable.ref
  data: GlobalCreditAccountDetailQuery["globalCreditAccount"] | null = null;

  transferCreditModalStore: TransferGlobalCreditModalStore;
  editModalStore: GlobalCreditAccountModalStore;

  private fetcher = new FetchHelper<
    GlobalCreditAccountDetailQuery,
    GlobalCreditAccountDetailQueryVariables
  >(GlobalCreditAccountDetailDocument);
  private updateMutator = new MutationHelper<
    UpdateGlobalCreditAccountMutation,
    UpdateGlobalCreditAccountMutationVariables
  >(UpdateGlobalCreditAccountDocument);

  constructor(
    private id: string,
    private appStore: AppStore,
  ) {
    makeObservable(this);
    this.editModalStore = new GlobalCreditAccountModalStore(appStore);
    this.transferCreditModalStore = new TransferGlobalCreditModalStore(
      appStore,
      undefined,
      true,
    );
    this.fetch(id);
  }

  @computed
  get isLoading() {
    return this.fetcher.isLoading.value;
  }

  @action.bound
  async fetch(id: string): Promise<void> {
    const [data, error] = await this.fetcher.fetch({
      id,
    });

    if (error) return;

    this.data = data.globalCreditAccount;
    this.transferCreditModalStore.setMaxCreditAmount(
      data.globalCreditAccount?.stats.remaining_credit
        ? nominate(
            data.globalCreditAccount.stats.remaining_credit,
            this.appStore.workspaceStore.settings?.currency.denomination ?? 2,
          )
        : undefined,
    );
  }

  @computed
  get isEditing() {
    return this.updateMutator.isLoading.value;
  }

  @action.bound
  async handleEditSubmit(
    input: UpdateGlobalCreditAccountInput | CreateGlobalCreditAccountInput,
  ) {
    const [, error] = await this.updateMutator.mutate({
      input: {
        id: this.id,
        title: input.title,
        note: input.note,
      },
    });

    if (error) return;

    this.editModalStore.modalState.onClose();
    await this.fetch(this.id);
  }
}
